import React, { useEffect, useState } from "react";
import { Dialog } from "components/dialog";
import { subscriptionPlansApi } from "api/models";
import { handleAxiosAndCaughtError, validateJson } from "utils";
import toast from "react-hot-toast";
import { Copy } from "lucide-react";
import PureDropDown, { DropdownOption } from "components/pure-dropdown";
import { SubscriptionPlan } from "types";
import { useTranslation } from "react-i18next";


interface CloneSubscriptionPlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subscriptionPlan: SubscriptionPlan | null;
  frequencyOptions: DropdownOption[];
}

const CloneSubscriptionPlanDialog: React.FC<CloneSubscriptionPlanDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  subscriptionPlan,
  frequencyOptions,
}) => {
  const { t } = useTranslation();
  const [isCloning, setIsCloning] = useState(false);
  const [form, setForm] = useState({
    name: "",
    price: 0,
    frequency: "" as string,
    configurations: {} as Record<string, any>,
    is_enterprise: false,
  });

  const [formText, setFormText] = useState({
    configurations: "{}",
  });

  const [jsonErrors, setJsonErrors] = useState({
    configurations: "",
  });

  // Options are provided by parent

  // Initialize state from incoming subscriptionPlan when dialog opens
  useEffect(() => {
    if (isOpen && subscriptionPlan) {
      const defaultName = `${subscriptionPlan.name} (Copy)`;
      const cfgText = JSON.stringify(subscriptionPlan.configurations || {}, null, 2);
      setForm({
        name: defaultName,
        price: subscriptionPlan.price ?? 0,
        frequency: subscriptionPlan.frequency || "",
        configurations: subscriptionPlan.configurations || {},
        is_enterprise: !!subscriptionPlan.is_enterprise,
      });
      setFormText({ configurations: cfgText });
      setJsonErrors({ configurations: validateJson(cfgText) });
    }
  }, [isOpen, subscriptionPlan]);

  const handleJsonChange = (value: string) => {
    setFormText(prev => ({ ...prev, configurations: value }));
    setJsonErrors(prev => ({ ...prev, configurations: validateJson(value) }));
  };

  const handleFrequencyChange = (value: string | string[]) => {
    const frequencyValue = Array.isArray(value) ? value[0] : value;
    setForm(prev => ({ ...prev, frequency: frequencyValue || "" }));
  };

  const handleClone = async () => {
    if (!form.name.trim()) {
      toast.error(t("subscriptionPlans.dialog.clone.messages.nameRequired"));
      return;
    }
    if (form.price < 0) {
      toast.error(t("subscriptionPlans.dialog.clone.messages.priceInvalid"));
      return;
    }
    if (jsonErrors.configurations) {
      toast.error(t("subscriptionPlans.dialog.clone.messages.fixJsonErrors"));
      return;
    }

    let parsedConfigurations = {} as Record<string, any>;
    try {
      parsedConfigurations = JSON.parse(formText.configurations);
    } catch (e) {
      toast.error(t("subscriptionPlans.dialog.clone.messages.invalidJson"));
      return;
    }

    setIsCloning(true);
    try {
      const payload = {
        name: form.name,
        price: form.price,
        frequency: form.frequency,
        configurations: parsedConfigurations,
        is_enterprise: !!form.is_enterprise,
      };
      await subscriptionPlansApi.create(payload);
      await onSuccess();
      toast.success(t("subscriptionPlans.dialog.clone.messages.clonedSuccessfully"));
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsCloning(false);
    }
  };

  if (!isOpen || !subscriptionPlan) return null;

  return (
    <Dialog isOpen={isOpen} setIsOpen={onClose} outsideClickDisabled={true} backdropHandlerDisabled={true}>
      <div className="flex min-h-[60vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
        <div className="border-b border-gray-200 pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("subscriptionPlans.dialog.clone.title")}
            </h2>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {t("subscriptionPlans.dialog.clone.description")}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptionPlans.dialog.clone.nameLabel")}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t("subscriptionPlans.dialog.clone.namePlaceholder")}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("subscriptionPlans.dialog.clone.priceLabel")}
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={Number.isNaN(form.price as number) ? 0 : form.price}
                onChange={(e) => setForm(prev => ({ ...prev, price: Number(e.target.value) }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder={t("subscriptionPlans.dialog.clone.pricePlaceholder")}
              />
            </div>
            <div>
              <PureDropDown
                options={frequencyOptions}
                value={form.frequency}
                onChange={handleFrequencyChange}
                placeholder={t("subscriptionPlans.dialog.clone.billingFrequencyPlaceholder")}
                label={t("subscriptionPlans.dialog.clone.billingFrequencyLabel")}
                showClearButton={false}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptionPlans.dialog.clone.configurationsLabel")}
            </label>
            <textarea
              value={formText.configurations}
              onChange={(e) => handleJsonChange(e.target.value)}
              className={`mt-1 w-full rounded-lg border px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 dark:bg-gray-700 dark:text-white ${
                jsonErrors.configurations 
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50 dark:bg-red-900/10' 
                  : 'border-gray-300 focus:border-blue-500 dark:border-gray-600'
              }`}
              rows={8}
            />
            {jsonErrors.configurations && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {jsonErrors.configurations}
              </div>
            )}
          </div>

        <div className="flex items-center gap-3">
          <input
            id="clone_is_enterprise"
            type="checkbox"
            checked={!!form.is_enterprise}
            onChange={(e) => setForm(prev => ({ ...prev, is_enterprise: e.target.checked }))}
            className="h-5 w-5 rounded-md border-gray-300 accent-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="clone_is_enterprise" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("subscriptionPlans.dialog.clone.enterprisePlanLabel")}
          </label>
        </div>
        </div>

        <div className="flex justify-end pt-3 sm:pt-4">
          <button
            type="button"
            onClick={handleClone}
            disabled={isCloning || !form.name.trim() || !!jsonErrors.configurations || form.price < 0}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-2 font-semibold text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCloning ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Copy size={16} />
            )}
            {t("subscriptionPlans.dialog.clone.cloneButton")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CloneSubscriptionPlanDialog;


